import React, { CSSProperties, useContext, useState } from "react";
import Tabs from "common/Tabs/Tabs";
import styled, { useTheme } from "styled-components";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import BotSummary from "features/bots/BotDrawer/BotSummary";
import BotChart from "features/bots/common/BotChart";
import PublicBotToggle from "features/bots/BotDrawer/PublicBotToggle";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  BacktestingRunType,
  BotData,
  BotGraphType,
  BotType,
} from "@shared/types";
import BotGeneralStatistics from "features/bots/BotDrawer/BotGeneralStatistics";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { BotDetailsContext } from "@shared/features/bots/BotDetails.context";
import DefaultGroupConfigurationMessage from "features/bots/BotForm/common/DefaultGroupConfigurationMessage";
import { sharedLib } from "@shared/services/shared.service.ts";
import { skipToken } from "@reduxjs/toolkit/query";

interface GeneralTabProps {
  botData?: BotData;
  botId: number;
  backtesting?: BacktestingRunType;
  isBacktesting?: boolean;
  style?: CSSProperties;
}

const GeneralTab = ({
  botData,
  botId,
  backtesting,
  isBacktesting,
  style,
}: GeneralTabProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { getBotDataStatus, isChannelConfiguration } =
    useContext(BotDetailsContext) || {};
  const [tab, setTab] = useState<BotGraphType>(
    BotGraphType.ACCUMULATING_PROFIT
  );
  const isSignalsBot = botData?.bot_type === BotType.SIGNAL;
  const isTradingViewSignalsBot =
    botData?.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const showGroupConfiguration = isSignalsBot && !isBacktesting;
  const showCharts =
    !isChannelConfiguration && !isBacktesting && !isTradingViewSignalsBot;
  const { isNewLook } = useTheme();

  const groupConfigurationStatus =
    sharedLib.sharedApi.bots.useGetSubGroupDefaultConfigurationQuery(
      showGroupConfiguration && botData?.exchange && botData?.sub_group_id
        ? { sub_group_id: botData?.sub_group_id, exchange: botData?.exchange }
        : skipToken
    );

  const getBotSummary = () => {
    if (!getBotDataStatus?.currentData) {
      return (
        <LoadingSpinnerOverlay
          isLoading={true}
          spinnerMarginTop={50}
          hideContentWhileLoading={true}
          wrap={true}
        />
      );
    } else if (botData) {
      return (
        <BotSummary
          botData={botData}
          showAccount={true}
          showAmount={true}
          style={{
            marginTop: showCharts ? 20 : 0,
          }}
          backtesting={backtesting}
        />
      );
    }
  };

  const tabsItems = [
    BotGraphType.ACCUMULATING_PROFIT,
    BotGraphType.PROFIT_BY_DAY,
  ].map((botGraphType) => ({
    key: botGraphType.toString(),
    label: t(BotGraphTypeToDisplay[botGraphType]),
    children: (
      <ChartWrapper>
        <BotChart
          botId={botId}
          chartType={botGraphType}
          botData={botData}
          mobileBreakpoint={Infinity}
          isBacktesting={isBacktesting}
        />
      </ChartWrapper>
    ),
  }));

  const newLookCharts = (
    <NewLookChartWrapper>
      <BotChart
        botId={botId}
        chartType={tab}
        botData={botData}
        mobileBreakpoint={Infinity}
        isBacktesting={isBacktesting}
        title={
          <SegmentTabs<string>
            options={NEW_LOOK_CHART_TYPES}
            value={String(tab)}
            onChange={(value) => setTab(Number(value) as BotGraphType)}
            ariaLabel={t("performance.chart_type_label")}
            getLabel={(value) =>
              t(BotGraphTypeToDisplay[Number(value) as BotGraphType])
            }
          />
        }
      />
    </NewLookChartWrapper>
  );

  const charts = isNewLook ? (
    newLookCharts
  ) : (
    <Tabs
      activeKey={tab.toString()}
      onTabClick={(tabKey: string) => setTab(parseInt(tabKey))}
      size="small"
      tabBarStyle={{
        margin: "0 0 8px 0",
      }}
      $hideBorder={true}
      items={tabsItems}
    />
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={
        !getBotDataStatus?.currentData ||
        (showGroupConfiguration && !groupConfigurationStatus.currentData)
      }
      spinnerSize="large"
      hideContentWhileLoading={true}
    >
      <div style={style}>
        {showCharts && (
          <>
            {<BotGeneralStatistics botData={botData} />}
            {charts}
          </>
        )}
        {showGroupConfiguration && (
          <DefaultGroupConfigurationMessage
            style={{ marginTop: 28, marginBottom: 28 }}
            subGroupId={botData?.sub_group_id}
            exchange={botData?.exchange}
          />
        )}
        {getBotSummary()}
        {botData && botData.bot_type === BotType.ASAP && !isBacktesting && (
          <PublicBotToggle botData={botData} />
        )}
      </div>
    </LoadingSpinnerOverlay>
  );
};

export default GeneralTab;

const ChartWrapper = styled.div`
  height: 300px;
`;
const NewLookChartWrapper = styled.div`
  height: 300px;
  margin-top: 24px;
`;
const NEW_LOOK_CHART_TYPES = [
  String(BotGraphType.ACCUMULATING_PROFIT),
  String(BotGraphType.PROFIT_BY_DAY),
];
